import React from 'react';

import { useTranslation } from '@/i18n';
import { PermissionGuard, SidePanelFormLayout } from '@/components/layout';
import { PrimaryButton } from '@/components/ui/buttons/PrimaryButton';
import { Selector } from '@/components/ui/inputs/selector';
import { DatePicker } from '@/components/ui/filters/DatePicker';
import { LabelInput } from '@/components/ui/inputs/LabelInput';
import type { HolidayByRegionItem } from '@/api/domains/attendance';
import { useHolidayByRegionFormPanel } from '@/hooks/attendance/holiday/useHolidayByRegionFormPanel';


interface HolidayByRegionFormPanelProps {
  holidayByRegionData?: HolidayByRegionItem;
  onSubmit?: () => void;
  readOnly?: boolean;
}

export const HolidayByRegionFormPanel: React.FC<HolidayByRegionFormPanelProps> = ({
  holidayByRegionData,
  onSubmit,
  readOnly,
}) => {
  const { t: translate } = useTranslation();

  const {
    isEditMode,
    holidayId,
    holidayTypeValue,
    holidayTypeOptions,
    regionValue,
    regionOptions,
    dateValue,
    reasonValue,
    effectiveReadOnly,
    isSubmitting,
    isReady,
    errors,
    isSubmitted,
    handleHolidayTypeChange,
    handleRegionChange,
    handleDateChange,
    handleReasonChange,
    handleSubmit,
  } = useHolidayByRegionFormPanel({ holidayByRegionData, onSubmit, readOnly });

  return (
    <SidePanelFormLayout onSubmit={handleSubmit} data-testid="holiday-by-region-form-panel">
      <SidePanelFormLayout.Fields>
        {isEditMode && holidayId !== null && (
          <LabelInput
            label={translate('attendance:holiday.form.idLabel')}
            value={String(holidayId)}
            disabled
            readOnly
            data-testid="holiday-by-region-id-input"
          />
        )}

        <Selector
          label={translate('attendance:holiday.form.holidayType')}
          placeholder={translate('attendance:holiday.form.holidayTypePlaceholder')}
          required
          options={holidayTypeOptions}
          value={holidayTypeValue}
          onChange={handleHolidayTypeChange}
          disabled={effectiveReadOnly || isSubmitting}
          color={errors.holidayTypeId && isSubmitted ? 'error' : 'default'}
          supportingText={
            errors.holidayTypeId?.message && isSubmitted
              ? translate(String(errors.holidayTypeId.message))
              : undefined
          }
          data-testid="holiday-by-region-type-selector"
        />

        <Selector
          label={translate('attendance:holiday.form.region')}
          placeholder={translate('attendance:holiday.form.regionPlaceholder')}
          required
          options={regionOptions}
          value={regionValue}
          onChange={handleRegionChange}
          disabled={effectiveReadOnly || isSubmitting}
          color={errors.regionId && isSubmitted ? 'error' : 'default'}
          supportingText={
            errors.regionId?.message && isSubmitted
              ? translate(String(errors.regionId.message))
              : undefined
          }
          data-testid="holiday-by-region-region-selector"
        />

        <DatePicker
          label={translate('attendance:holiday.form.date')}
          value={dateValue}
          onChange={handleDateChange}
          disabled={effectiveReadOnly || isSubmitting}
          color={errors.date && isSubmitted ? 'error' : undefined}
          supportingText={
            errors.date?.message && isSubmitted
              ? translate(String(errors.date.message))
              : undefined
          }
          placeholder={translate('attendance:holiday.form.datePlaceholder')}
          required
          data-testid="holiday-by-region-date-picker"
        />

        <LabelInput
          label={translate('attendance:holiday.form.reason')}
          placeholder={translate('attendance:holiday.form.reasonPlaceholder')}
          value={reasonValue}
          onChange={handleReasonChange}
          required
          disabled={effectiveReadOnly || isSubmitting}
          color={errors.reason && isSubmitted ? 'error' : 'default'}
          supportingText={
            errors.reason?.message && isSubmitted
              ? translate(String(errors.reason.message))
              : undefined
          }
          data-testid="holiday-by-region-reason-input"
        />
      </SidePanelFormLayout.Fields>

      {!effectiveReadOnly && (
        <SidePanelFormLayout.Footer>
          <PermissionGuard action={isEditMode ? 'edit' : 'create'}>
            <PrimaryButton
              type="submit"
              fullWidth
              isLoading={isSubmitting}
              disabled={!isReady || isSubmitting}
            >
              {translate('attendance:holiday.form.submit')}
            </PrimaryButton>
          </PermissionGuard>
        </SidePanelFormLayout.Footer>
      )}
    </SidePanelFormLayout>
  );
};

export default HolidayByRegionFormPanel;